import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, Router } from '@angular/router';
import { ErrorZone } from '../enums/error-zone';
import { ErrorService } from '../services/error.service';
import { parsePositiveIdFromUrlToken } from '../utils/route-param-id';

/**
 * Creates a guard that blocks routes whose `paramName` route parameter is not a positive
 * integer entity ID, redirecting to `fallbackUrl` and showing a generic error toast.
 *
 * @param paramName Name of the route parameter holding the ID, e.g. `'caseId'`.
 * @param fallbackUrl URL to redirect to when the ID is missing or invalid, or a function of the
 * route snapshot computing one — e.g. to redirect back into a parent's still-valid context
 * (`(route) => \`/admin/case/${route.paramMap.get('caseId')}/clients\``) rather than a fixed URL.
 * @param errorZone Zone determining the generic error toast content.
 */
export function positiveIdParamGuard(
    paramName: string,
    fallbackUrl: string | ((route: ActivatedRouteSnapshot) => string),
    errorZone: ErrorZone
): CanActivateFn {
    return (route) => {
        if (
            parsePositiveIdFromUrlToken(route.paramMap.get(paramName)) != null
        ) {
            return true;
        }

        inject(ErrorService).generalToast(errorZone);

        const resolvedUrl =
            typeof fallbackUrl === 'function'
                ? fallbackUrl(route)
                : fallbackUrl;

        return inject(Router).parseUrl(resolvedUrl);
    };
}
